import { Reducer, useCallback, useReducer } from 'react';

import { ApiStatus, API_STATUS } from '@/constants';

import {
  planInfoApi,
  PlanInfoApiResponse,
  isPlanInfoApiResultCode,
  PLAN_INFO_API_RESULT_CODE,
  PlanInfoApiRequest,
} from '../planInfoApi';

/**
 * State
 */
export type PlanInfoState = {
  // レスポンスデータ
  data?: PlanInfoApiResponse;
  // APIの呼び出し状態
  status: ApiStatus;
};

/**
 * Actionタイプ
 */
const PLAN_INFO_ACTION_TYPE = {
  SET_PLAN_INFO: 'SET_PLAN_INFO',
  SET_PLAN_INFO_SUCCESS: 'SET_PLAN_INFO_SUCCESS',
  SET_PLAN_INFO_FAILED: 'SET_PLAN_INFO_FAILED',
} as const;

/**
 * Action
 */
type PlanInfoAction = {
  data?: PlanInfoApiResponse;
  type: keyof typeof PLAN_INFO_ACTION_TYPE;
};

/**
 * reducer関数
 *
 * @param state
 * @param action
 * @returns
 */
const reducer: Reducer<PlanInfoState, PlanInfoAction> = (
  state: PlanInfoState,
  action: PlanInfoAction,
) => {
  switch (action.type) {
    case PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO:
      return {
        ...state,
        status: API_STATUS.LOADING,
      };
    case PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO_SUCCESS:
      return {
        ...state,
        data: action.data,
        status: API_STATUS.SUCCESS,
      };
    case PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO_FAILED:
      return {
        ...state,
        data: action.data,
        status: API_STATUS.FAILED,
      };
    default:
      return state;
  }
};

/**
 * プラン情報取得API 呼び出しhooks
 *
 * @returns
 */
export const usePlanInfoApi = () => {
  const [state, dispatch] = useReducer(reducer, {
    data: undefined,
    status: API_STATUS.IDLE,
  });

  /**
   * API呼び出し
   */
  const fetchPlanInfo = useCallback(
    (request: PlanInfoApiRequest, isPrivate?: boolean) => {
      if (state.status === API_STATUS.LOADING) {
        return;
      }

      dispatch({ type: PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO });

      planInfoApi(request, isPrivate)
        .then((response: PlanInfoApiResponse) => {
          // 成功
          if (response.resultCode === PLAN_INFO_API_RESULT_CODE.OK) {
            dispatch({
              type: PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO_SUCCESS,
              data: response,
            });

            return;
          }

          // 意図しない結果コードの型だった場合は失敗とする
          if (!isPlanInfoApiResultCode(response.resultCode)) {
            dispatch({
              type: PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO_FAILED,
              data: {
                ...response,
                resultCode: PLAN_INFO_API_RESULT_CODE.ERR_UNKNOWN,
              } as PlanInfoApiResponse,
            });

            return;
          }

          // 失敗
          dispatch({
            type: PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO_FAILED,
            data: response,
          });
        })
        .catch((_) => {
          dispatch({
            type: PLAN_INFO_ACTION_TYPE.SET_PLAN_INFO_FAILED,
            data: {
              resultCode: PLAN_INFO_API_RESULT_CODE.ERR_UNKNOWN,
            } as PlanInfoApiResponse,
          });
        });
    },
    [state.status],
  );

  return {
    planInfoState: state,
    fetchPlanInfo,
  };
};
